import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Copy, Download, FileText, Loader2, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { formatDateTime } from '../../utils/api';

const absoluteUrl = (path) => `${window.location.origin}${path}`;

// Phase 41.5 — Lead Blueprint: Super Admin / Vyapaar Ops only. Generates a
// customer-blind, Meshora/Vyapaar-branded PDF they can download or share via a
// public link with a Selling Partner, without exposing customer identity or
// internal commission figures.
export const BlueprintShareDialog = ({ open, onOpenChange, leadId }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}/blueprint/links`);
      setLinks(res.data.filter((l) => !l.revoked));
    } catch (e) {
      toast.error('Failed to load existing Blueprint links');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open) fetchLinks();
  }, [open, fetchLinks]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/leads/${leadId}/blueprint`);
      const link = absoluteUrl(res.data.download_path);
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Blueprint generated — link copied to clipboard');
      } catch (e) {
        toast.success('Blueprint generated');
      }
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate Blueprint');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (path) => {
    try {
      await navigator.clipboard.writeText(absoluteUrl(path));
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const handleRevoke = async (linkId) => {
    if (!window.confirm('Revoke this Blueprint link? It will stop working immediately.')) return;
    try {
      await api.delete(`/blueprint/${linkId}`);
      toast.success('Link revoked');
      fetchLinks();
    } catch (e) {
      toast.error('Failed to revoke link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="blueprint-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Share Blueprint
          </DialogTitle>
          <DialogDescription>
            A customer-blind PDF summary you can share with a Selling Partner — no customer
            name/contact details and no internal commission figures.
          </DialogDescription>
        </DialogHeader>

        <Button onClick={handleGenerate} disabled={generating} data-testid="generate-blueprint-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
          Generate New Blueprint Link
        </Button>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
          {!loading && links.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="no-blueprint-links">
              No Blueprint links generated yet.
            </p>
          )}
          {links.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between gap-2 border rounded-lg p-3"
              data-testid={`blueprint-link-${link.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{formatDateTime(link.created_at)}</p>
                <p className="text-xs text-muted-foreground truncate">by {link.created_by_name}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="icon" variant="ghost" title="Copy link"
                  onClick={() => handleCopy(link.download_path)}
                  data-testid={`copy-blueprint-${link.id}`}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  size="icon" variant="ghost" title="Open / Download PDF" asChild
                  data-testid={`download-blueprint-${link.id}`}
                >
                  <a href={absoluteUrl(link.download_path)} target="_blank" rel="noreferrer">
                    <Download className="w-4 h-4" />
                  </a>
                </Button>
                <Button
                  size="icon" variant="ghost" title="Revoke link"
                  onClick={() => handleRevoke(link.id)}
                  data-testid={`revoke-blueprint-${link.id}`}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BlueprintShareDialog;
